import React, { useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  IconButton,
  Tooltip
} from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import ChatIcon from '@material-ui/icons/Chat';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import { Draggable } from 'react-beautiful-dnd';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { startConversation } from '../../../redux/features/messages/messagesSlice';
import { currentCompanySelector } from '../../../redux/features/company/companiesSlice';
import { APP_PATH_MESSAGES } from '../../../config';
import { ScheduleInterviewDialog } from '../../../components';
import {
  trackConversationStarted,
  trackCandidateProfileView,
  trackCandidateMessageClick,
  trackCandidateCardInteraction,
  trackScheduleInterviewDialogOpened
} from '../../../helpers/analytics';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing'
    },
    transition: 'box-shadow 0.2s ease-in-out',
    '&:hover': {
      boxShadow: theme.shadows[4]
    },
    '&:hover $cardActions': {
      [theme.breakpoints.up('md')]: {
        opacity: 1,
        pointerEvents: 'auto'
      }
    }
  },
  cardDragging: {
    boxShadow: theme.shadows[8],
    transform: 'rotate(2deg)'
  },
  candidateName: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main
  },
  jobTitle: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1)
  },
  dateChip: {
    height: 24,
    fontSize: '0.75rem'
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  viewProfileButton: {
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  messageButton: {
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  scheduleButton: {
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  scorecardButton: {
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  actionIconButton: {
    padding: theme.spacing(0.75)
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  cardActions: {
    [theme.breakpoints.up('md')]: {
      opacity: 0,
      pointerEvents: 'none',
      transition: 'opacity 0.2s ease-in-out'
    }
  },
  cardActionsVisible: {
    [theme.breakpoints.up('md')]: {
      opacity: 1,
      pointerEvents: 'auto'
    }
  }
}));

const ApplicationCard = ({ application, index }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const company = useSelector(currentCompanySelector);
  
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleViewProfile = (e) => {
    e.stopPropagation();
    
    // Track profile view click
    trackCandidateProfileView(
      application.id,
      application.userId,
      application.candidateName || 'Unknown',
      application.status,
      'kanban_card'
    );
    
    console.log('👤 [ApplicationCard] Viewing candidate profile:', application.userId);
    history.push(`/candidate/${application.userId}`);
  };

  const handleMessageCandidate = (e) => {
    e.stopPropagation();
    
    // Track message click
    trackCandidateMessageClick(
      application.id,
      application.userId,
      application.candidateName || 'Unknown',
      application.status,
      'kanban_card'
    );
    
    console.log('💬 [ApplicationCard] Starting conversation with candidate:', application.userId);
    
    // Start a conversation with the candidate
    dispatch(
      startConversation({
        recipientUserId: application.userId,
        initialMessage: null // No initial message, just start the conversation
      })
    )
      .then((result) => {
        console.log('✅ [ApplicationCard] Conversation started, navigating to messages');
        
        // Track conversation started
        if (result?.payload?.conversation?.id) {
          trackConversationStarted(
            result.payload.conversation.id,
            'candidate',
            'applications_list'
          );
        }
        
        // Navigate to messages page
        history.push(APP_PATH_MESSAGES);
        return { success: true, result };
      })
      .catch((error) => {
        console.error('❌ [ApplicationCard] Failed to start conversation:', error);
        return { success: false, error };
      });
  };

  const handleScheduleInterview = (e) => {
    e.stopPropagation();
    
    console.log('📅 [ApplicationCard] Opening schedule interview dialog');
    setScheduleDialogOpen(true);

    // Track analytics
    if (window.gtag) {
      trackScheduleInterviewDialogOpened(
        application.userId,
        application.candidateName || 'Unknown',
        application.jobId,
        application.jobTitle,
        'application_card'
      );
    }
  };

  const handleCloseScheduleDialog = () => {
    setScheduleDialogOpen(false);
  };

  const handleViewScorecard = (e) => {
    e.stopPropagation();
    history.push(`/applications/${application.id}/scorecard`);
  };

  const handleCardClick = (e) => {
    // Only track card click if it's not coming from a button
    if (!e.target.closest('button')) {
      trackCandidateCardInteraction(
        application.id,
        application.candidateName || 'Unknown',
        application.status,
        'card_hover'
      );
    }
  };

  return (
    <>
    <Draggable draggableId={application.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}>
          <Card
            className={`${classes.card} ${
              snapshot.isDragging ? classes.cardDragging : ''
            }`}
            onClick={handleCardClick}>
            <CardContent>
              <Typography variant="h6" className={classes.candidateName}>
                {application.candidateName || 'Unknown Candidate'}
              </Typography>

              <Typography variant="body2" className={classes.jobTitle}>
                Applied to: <strong>{application.jobTitle}</strong>
              </Typography>

              {application.coverLetter && (
                <Box mt={1} mb={1}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                    {application.coverLetter}
                  </Typography>
                </Box>
              )}

              <div className={classes.footer}>
                <Chip
                  label={formatDate(application.applicationDate)}
                  size="small"
                  className={classes.dateChip}
                  variant="outlined"
                />
                <div
                  className={`${classes.buttonGroup} ${classes.cardActions} ${
                    snapshot.isDragging ? classes.cardActionsVisible : ''
                  }`}>
                  <Tooltip title="View">
                    <IconButton
                      size="small"
                      aria-label="View profile"
                      className={`${classes.actionIconButton} ${classes.viewProfileButton}`}
                      onClick={handleViewProfile}>
                      <PersonIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Schedule">
                    <IconButton
                      size="small"
                      aria-label="Schedule interview"
                      className={`${classes.actionIconButton} ${classes.scheduleButton}`}
                      onClick={handleScheduleInterview}>
                      <CalendarTodayIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="View Scorecard">
                    <IconButton
                      size="small"
                      aria-label="View scorecard"
                      className={`${classes.actionIconButton} ${classes.scorecardButton}`}
                      onClick={handleViewScorecard}>
                      <AssignmentOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Message">
                    <IconButton
                      size="small"
                      aria-label="Message candidate"
                      className={`${classes.actionIconButton} ${classes.messageButton}`}
                      onClick={handleMessageCandidate}>
                      <ChatIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Draggable>
    
    {/* Schedule Interview Dialog */}
    {scheduleDialogOpen && (
      <ScheduleInterviewDialog
        open={scheduleDialogOpen}
        onClose={handleCloseScheduleDialog}
        candidateUserId={application.userId}
        candidateName={application.candidateName || 'Candidate'}
        candidateEmail={application.candidateEmail || ''}
        jobId={application.jobId}
        jobTitle={application.jobTitle}
        applicationId={application.id}
        companyId={company?.id || ''}
      />
    )}
  </>
  );
};

ApplicationCard.propTypes = {
  application: PropTypes.shape({
    id: PropTypes.string.isRequired,
    userId: PropTypes.string.isRequired,
    candidateName: PropTypes.string,
    jobTitle: PropTypes.string.isRequired,
    applicationDate: PropTypes.number.isRequired,
    status: PropTypes.string.isRequired,
    coverLetter: PropTypes.string
  }).isRequired,
  index: PropTypes.number.isRequired
};

export default React.memo(ApplicationCard);
